/* eslint-disable max-lines */
/**
 * @heading1: Helper
 * @heading3: isErrorWithMessage function
 * @block_quote: Checks if the given error object contains a message property of type string.
 * @ol:
 * error : {unknown} - The error object to be checked.
 * @block_quote: Return: {boolean} Returns true if the error object has a 'message' property of type string, otherwise false.
 * @code:
 * const error = { message: 'Something went wrong' };
 * if (isErrorWithMessage(error)) {
 *     console.log(error.message);
 * }
 */

import { brand } from '../theme/themePrimitives';
import { blue, grey, orange, red } from '@mui/material/colors';

export const isErrorWithMessage = (error: unknown): error is { message: string } => {
    return (
        typeof error === 'object' &&
        error !== null &&
        'message' in error &&
        typeof (error as { message: unknown }).message === 'string'
    );
};

export const formatDate = (date: string, options: {
    month?: 'short' | 'long' | 'numeric' | 'narrow' | '2-digit',
    day?: '2-digit' | 'numeric',
    year?: '2-digit' | 'numeric'
} = { month: 'short', day: '2-digit' }): string => {
    // Parse the date string
    const dateObj = date ? new Date(date) : null;

    // Validate the date
    if (!dateObj || isNaN(dateObj.getTime())) {
        console.error('Invalid date provided:', date);
        return '';
    }

    // Format the valid date
    return new Intl.DateTimeFormat('en-US', {
        month: options.month,
        day: options.day,
        year: options.year
    }).format(dateObj);
};

export const formatTime = (timestamp: string, options: {
    hour?: '2-digit' | 'numeric',
    minute?: '2-digit' | 'numeric',
    hour12?: boolean
} = {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }): string => {
    // Parse the date string
    const dateObj = timestamp ? new Date(timestamp) : null;

    // Validate the date
    if (!dateObj || isNaN(dateObj.getTime())) {
        console.error('Invalid date provided:', timestamp);
        return '';
    }

    // Format the valid date
    return new Intl.DateTimeFormat('en-US', {
        hour: options.hour,
        minute: options.minute,
        hour12: options.hour12
    }).format(dateObj);
};


export const timestampToFormattedDate = (isoTimestamp: string, options: {
    month?: 'short' | 'long' | 'numeric' | 'narrow' | '2-digit',
    day?: '2-digit' | 'numeric',
    year?: '2-digit' | 'numeric'
    hour?: '2-digit' | 'numeric',
    minute?: '2-digit' | 'numeric',
    hour12?: boolean
} = {
        month: 'short',
        day: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
    }): string => {
    const dateObject = new Date(isoTimestamp);
    const data: Intl.DateTimeFormatOptions = {
        month: options.month,
        day: options.day,
        year: options.year,
        hour: options.hour,
        minute: options.minute,
        hour12: options.hour12
    };
    return dateObject.toLocaleString('en-US', data);
};


export const badgeColors = {
    'badge-warning': orange[100],
    'badge-primary': brand[100],
    'badge-success': blue[100],
    'badge-secondary': grey[100],
    'badge-danger': red[100]
};

export const fetchImageFormatFromBase64 = (base64File: string) => {
    if (base64File.startsWith('data:')) {
        // Extract the MIME type between 'data:' and ';base64,'
        const mimeType = base64File.substring(5, base64File.indexOf(';base64'));

        // Map common MIME types to file formats
        const mimeToFormatMap: Record<string, string> = {
            'image/png': 'PNG Image',
            'image/jpeg': 'JPEG Image',
            'image/gif': 'GIF Image',
            'application/pdf': 'PDF Document',
            'text/plain': 'Text File',
            'application/msword': 'Word Document',
            'application/vnd.openxmlformats-officedocument.wordprocessingml.document': 'Word Document (DOCX)',
            'application/vnd.ms-excel': 'Excel Spreadsheet',
            'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet': 'Excel Spreadsheet (XLSX)',
            'application/vnd.ms-powerpoint': 'PowerPoint Presentation',
            'application/vnd.openxmlformats-officedocument.presentationml.presentation': 'PowerPoint Presentation (PPTX)',
            'application/zip': 'ZIP Archive',
            'application/json': 'JSON File',
            'text/html': 'HTML Document',
            'application/xml': 'XML Document'
        };

        // Return the mapped format if available, otherwise return the MIME type
        return mimeToFormatMap[mimeType] || `Unknown format (${mimeType})`;
    }
    return 'Invalid Base64 String';
};

export const extractSizeOfFile = (base64File: string): number => {
    if (!base64File.startsWith('data:') || !base64File.includes(';base64,')) {
        throw new Error('Invalid base64 file string');
    }

    // Extract the actual Base64 content using destructuring
    const [, base64Content] = base64File.split(';base64,');

    // Calculate the file size in bytes
    const padding = base64Content.endsWith('==') ? 2 : base64Content.endsWith('=') ? 1 : 0;
    const fileSizeInBytes = (((base64Content.length * 3) / 4) - padding);

    // Return the size in bytes (rounded)
    return Math.round(fileSizeInBytes);
};

const getOrdinalSuffix = (day: number): string => {
    if (day > 3 && day < 21) return 'th';
    switch (day % 10) {
        case 1: return 'st';
        case 2: return 'nd';
        case 3: return 'rd';
        default: return 'th';
    }
};

export const formatDateWithTime = (dateString: string): string => {
    const date = new Date(dateString);

    // Get month, day, and year
    const options: Intl.DateTimeFormatOptions = { month: 'short' };
    const month = new Intl.DateTimeFormat('en-US', options).format(date);

    const day = date.getDate();
    const year = date.getFullYear();

    // Add ordinal suffix to the day
    const dayWithSuffix = `${day}${getOrdinalSuffix(day)}`;

    // Get the time
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const formattedTime = `${(hours % 12 || 12)}:${minutes.toString().padStart(2, '0')} ${hours >= 12 ? 'pm' : 'am'}`;

    // Combine the results
    return `${month} ${dayWithSuffix}, ${year}, ${formattedTime}`;
};
// Helper Functions
const formatSeconds = (seconds: number): string => (seconds === 1 ? '1 second ago' : `${seconds} seconds ago`);

const formatMinutes = (seconds: number): string => {
    const minutes = Math.floor(seconds / 60);
    return minutes === 1 ? '1 minute ago' : `${minutes} minutes ago`;
};

const formatHours = (seconds: number): string => {
    const hours = Math.floor(seconds / 3600);
    return hours === 1 ? '1 hour ago' : `${hours} hours ago`;
};

const formatDays = (seconds: number): string => {
    const days = Math.floor(seconds / 86400);
    return days === 1 ? '1 day ago' : `${days} days ago`;
};

const formatWeeks = (seconds: number): string => {
    const weeks = Math.floor(seconds / 604800);
    return weeks === 1 ? '1 week ago' : `${weeks} weeks ago`;
};

const formatMonths = (seconds: number): string => {
    const months = Math.floor(seconds / 2419200);
    return months === 1 ? '1 month ago' : `${months} months ago`;
};

const formatYears = (seconds: number): string => {
    const years = Math.floor(seconds / 29030400);
    return years === 1 ? '1 year ago' : `${years} years ago`;
};

export const formatTimeUptoNow = (time: string): string => {
    if (!time) return '';
    const now = new Date();
    const createdDate = new Date(time);
    const diffInSeconds = Math.floor((now.getTime() - createdDate.getTime()) / 1000);
    if (diffInSeconds < 60) return formatSeconds(diffInSeconds);
    if (diffInSeconds < 3600) return formatMinutes(diffInSeconds);
    if (diffInSeconds < 86400) return formatHours(diffInSeconds);
    if (diffInSeconds < 604800) return formatDays(diffInSeconds);
    if (diffInSeconds < 2419200) return formatWeeks(diffInSeconds);
    if (diffInSeconds < 29030400) return formatMonths(diffInSeconds);

    return formatYears(diffInSeconds);
};

export const formatTimeRemaining = (seconds: number) => {
    if (seconds < 60) return `${seconds} sec`;
    if (seconds < 3600) return `${Math.floor(seconds / 60)} min`;
    if (seconds < 86400) return `${Math.floor(seconds / 3600)} hrs`;
    if (seconds < 604800) return `${Math.floor(seconds / 86400)} days`;
    if (seconds < 2419200) return `${Math.floor(seconds / 604800)} weeks`;
    return `${Math.floor(seconds / 2419200)} months`;
};


export const currencyOptions = [
    { value: 'usd', label: 'USD' },
    { value: 'eur', label: 'EUR' },
    { value: 'cad', label: 'CAD' },
    { value: 'aud', label: 'AUD' }
];

export const intervalOptions = [
    { value: 'day', label: 'Daily' },
    { value: 'week', label: 'Weekly' },
    { value: 'month', label: 'Monthly' },
    { value: 'three', label: 'Every 3 months' },
    { value: 'six', label: 'Every 6 months' },
    { value: 'year', label: 'Yearly' }
];

export const fieldsOptions = [
    { value: 'company_name', label: 'company_name' },
    { value: 'company_phone_number', label: 'company_phone_number' },
    { value: 'company_email_address', label: 'company_email_address' },
    { value: 'company_address', label: 'company_address' },
    { value: 'company_city', label: 'company_city' },
    { value: 'company_website', label: 'company_website' },
    { value: 'contact_email_address', label: 'contact_email_address' },
    { value: 'contact_name', label: 'contact_name' },
    { value: 'contact_phone_number', label: 'contact_phone_number' },
    { value: 'custom_field_1', label: 'custom_field_1' },
    { value: 'custom_field_2', label: 'custom_field_2' },
    { value: 'custom_field_3', label: 'custom_field_3' },
    { value: 'custom_field_4', label: 'custom_field_4' },
    { value: 'custom_field_5', label: 'custom_field_5' }
];

export const emailAndPhoneOptions = [
    { value: 'email', label: 'Email' },
    { value: 'phone', label: 'Phone' }
];

export const timelineOptions = [
    { value: 'sms', label: 'SMS' },
    { value: 'email', label: 'Email' },
    { value: 'note', label: 'Notes' },
    { value: 'document', label: 'Documents' },
    { value: 'reminder', label: 'Reminders' },
    { value: 'calls', label: 'Calls' },
    { value: 'contacts', label: 'Contacts' },
    { value: 'general', label: 'View All' }
];
export const isHTML = (str: string) => (/<\/?[a-z][\s\S]*>/iu).test(str);

export const days = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'];

export const timesData = Array.from({ length: 96 }, (_, i) => {
    const totalMinutes = i * 15;
    const hour = Math.floor(totalMinutes / 60);
    const minute = totalMinutes % 60;
    const h = hour % 12 || 12;
    const ampm = hour < 12 ? 'AM' : 'PM';
    return `${h.toString().padStart(2, '0')}:${minute.toString().padStart(2, '0')} ${ampm}`;
});